/**
 * ModeTransition — animated surface keyed to a CSK structural mode.
 *
 * Renders a bordered surface whose accent color is driven by the
 * canonical `MODE_COLOR` token for the supplied `mode` (one of
 * `immune` / `inert` / `contained`). When the `mode` prop changes
 * (e.g. a proof transitions a guarantee from `INERT` to `IMMUNE`),
 * `motion` morphs `borderColor`, `backgroundColor`, and `boxShadow`
 * to the new mode's palette.
 *
 * Honors `prefers-reduced-motion`: collapses to an instant color
 * swap with no transition.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { motion, useReducedMotion } from "motion/react";
import { MODE_COLOR, withAlpha } from "../../design-system/tokens.js";
import { motionDurations, motionEasings } from "../../styling/motionTokens.js";

type ModeKey = keyof typeof MODE_COLOR;

export interface ModeTransitionProps {
  /** Current structural mode — drives the accent palette. */
  readonly mode: ModeKey;
  /** Wrapped content. */
  readonly children?: React.ReactNode;
  /** Optional className passthrough. */
  readonly className?: string;
}

function paletteFor(mode: ModeKey): {
  border: string;
  background: string;
  shadow: string;
  text: string;
} {
  const palette = MODE_COLOR[mode];
  return {
    border: palette.primary,
    // 10% wash of the primary — readable on any background. Sourced
    // through the shared `withAlpha` helper so alpha derivation stays
    // consistent with every other consumer that needs a low-opacity tint.
    background: withAlpha(palette.primary, "1A"),
    shadow: `0 0 24px ${withAlpha(palette.primary, "33")}`,
    text: palette.dim,
  };
}

export const ModeTransition = React.memo(function ModeTransition({
  mode,
  children,
  className,
}: ModeTransitionProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const palette = paletteFor(mode);
  const baseStyle = {
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: "0.5rem",
    padding: "0.75rem 1rem",
  };
  if (prefersReducedMotion) {
    return (
      <div
        className={className}
        data-testid="mode-transition"
        data-mode={mode}
        style={{
          ...baseStyle,
          borderColor: palette.border,
          backgroundColor: palette.background,
          color: palette.text,
          boxShadow: palette.shadow,
        }}
      >
        {children}
      </div>
    );
  }
  return (
    <motion.div
      className={className}
      data-testid="mode-transition"
      data-mode={mode}
      style={{
        ...baseStyle,
      }}
      animate={{
        borderColor: palette.border,
        backgroundColor: palette.background,
        color: palette.text,
        boxShadow: palette.shadow,
      }}
      transition={{
        duration: motionDurations.morph,
        ease: motionEasings.emphasized,
      }}
    >
      {children}
    </motion.div>
  );
});
